import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Delta from "quill-delta";

import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";

import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

import FormatColorTextOutlinedIcon from "@mui/icons-material/FormatColorTextOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import api from "../../../../api/api";
import { File_Upload_Wholesale } from "../../../../config";

import TitleIcon from "@mui/icons-material/Title";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import HighlightIcon from "@mui/icons-material/Highlight";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import CodeIcon from "@mui/icons-material/Code";
import ClearIcon from "@mui/icons-material/Clear";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { onErrorAlert } from "../../../../actions";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";


const MAX_SIZE = 2 * 1024 * 1024;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const ALLOW_MIME = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const ALLOW_EXT = [
    ".jpg", ".jpeg", ".png", ".gif", ".webp",
    ".pdf",
    ".doc", ".docx",
    ".xls", ".xlsx", "csv"
];

const IMAGE_MIME = ["image/jpeg", "image/png", "image/gif", "image/webp"];


export const ReplyEditor = ({
                                onSend,
                                onChange,
                                quote,
                                onClearQuote,
                                editing,
                                minHeight = 35,
                                maxFiles = 5,
                                mode = 'send',
                                onCancel,
                                ticketId,
                                initialHtml = "<p>Hi team,\u00a0\u00a0</p>",
                            }) => {
    const [value, setValue] = useState("");
    const [files, setFiles] = useState([]);
    const [showToolbar, setShowToolbar] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [imageUploading, setImageUploading] = useState(0);

    useEffect(() => {
        if (!editing && mode === 'send') {
            setValue(initialHtml);
            setTimeout(() => {
                const quill = getQuill();
                if (!quill) return;
                const length = quill.getLength() - 1;
                quill.setSelection(length, 0);
                quill.focus();
            }, 100);
        }
    }, [ticketId]);

    useEffect(() => {
        if (editing) {
            setValue(editing.html || "");
            setFiles(
                editing.files?.map(f => ({
                    id: Date.now() + Math.random(),
                    name: f.name || "file",
                    url: f.url,
                })) || []
            );
        }
    }, [editing]);

    const wrapperRef = useRef();
    const fileInputRef = useRef();
    const imageInputRef = useRef();
    const quillRef = useRef();
    const moreBtnRef = useRef(null);
    // ✅ 用 ref 保存 insertImageToEditor 最新引用，避免 paste/drop 闭包拿到旧版本
    const insertImageRef = useRef(null);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const open = Boolean(anchorEl);
    const darkMode = useSelector(state => state?.common?.darkMode);

    const ACCEPT_TYPES = ALLOW_EXT.join(",");

    const validateFile = (file) => {
        if (file.size > MAX_SIZE) {
            dispatch(onErrorAlert("File size cannot exceed 2MB"));
            return false;
        }
        if (file.name.length > 120) {
            dispatch(onErrorAlert("File name cannot exceed 120 characters"));
            return false;
        }
        const invalidName = /[\\/:*?"<>|&%#@!！？，。；：""''（）【】]/;
        if (invalidName.test(file.name)) {
            dispatch(onErrorAlert("File name contains invalid characters"));
            return false;
        }
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        if (!ALLOW_EXT.includes(ext)) {
            dispatch(onErrorAlert("Invalid file type"));
            return false;
        }
        if (file.type && !ALLOW_MIME.includes(file.type)) {
            dispatch(onErrorAlert("Invalid file MIME type"));
            return false;
        }
        return true;
    };

    const stripHtml = (html) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.innerText || div.textContent || "";
    };

    const cleanHtml = (html) => {
        if (!html) return "";
        let cleaned = html.replace(/(<p><br><\/p>)+/g, "<p><br></p>");
        cleaned = cleaned.replace(/^(<p><br><\/p>)+/, "");
        cleaned = cleaned.replace(/(<p><br><\/p>)+$/, "");
        return cleaned.trim();
    };

    const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post(File_Upload_Wholesale, formData);
        if (res?.status === 200 && res.data.code === 200) {
            return res.data.data.url;
        } else {
            dispatch(onErrorAlert(res.data.msg));
            throw new Error(res?.data?.msg || "Upload Error");
        }
    };

    const getQuill = () => quillRef.current?.getEditor();

    // ✅ useCallback 确保依赖稳定，同时每次渲染后同步到 ref
    const insertImageToEditor = useCallback(async (file) => {
        if (!IMAGE_MIME.includes(file.type)) {
            dispatch(onErrorAlert("Only image files (jpg/png/gif/webp) can be inserted inline"));
            return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
            dispatch(onErrorAlert("Image size cannot exceed 5MB"));
            return;
        }

        const quill = getQuill();
        if (!quill) return;

        setImageUploading(n => n + 1);
        try {
            const url = await uploadFile(file);
            const range = quill.getSelection(true);
            const index = range ? range.index : quill.getLength();
            quill.insertEmbed(index, "image", url, "user");
            quill.setSelection(index + 1, 0);
        } catch {
            // uploadFile 内部已 dispatch 错误
        } finally {
            setImageUploading(n => n - 1);
        }
    }, [dispatch]);

    // ✅ 每次渲染都同步最新函数到 ref
    useEffect(() => {
        insertImageRef.current = insertImageToEditor;
    });

    // ── 注册 paste / drop / clipboard matchers（只跑一次，通过 ref 调用最新函数）
    useEffect(() => {
        const quill = getQuill();
        if (!quill) return;

        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
                if (item.kind === "file" && IMAGE_MIME.includes(item.type)) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) insertImageRef.current?.(file);  // ✅ 通过 ref 调用
                    return;
                }
            }
        };

        const handleDrop = (e) => {
            const droppedFiles = e.dataTransfer?.files;
            if (!droppedFiles || droppedFiles.length === 0) return;
            const imageFile = Array.from(droppedFiles).find(f => IMAGE_MIME.includes(f.type));
            if (imageFile) {
                e.preventDefault();
                insertImageRef.current?.(imageFile);  // ✅ 通过 ref 调用
            }
        };

        quill.clipboard.addMatcher("IMG", (node) => {
            const src = node.getAttribute("src") || "";
            if (src.startsWith("http")) {
                return new Delta().insert({ image: src });
            }
            return new Delta();
        });

        quill.clipboard.addMatcher("TABLE", () => new Delta());
        quill.clipboard.addMatcher("IFRAME", () => new Delta());

        const editorRoot = quill.root;
        editorRoot.addEventListener("paste", handlePaste);
        editorRoot.addEventListener("drop", handleDrop);

        return () => {
            editorRoot.removeEventListener("paste", handlePaste);
            editorRoot.removeEventListener("drop", handleDrop);
        };
    }, []); // 只注册一次，paste/drop 内部通过 ref 拿最新函数

    // 点击外部关闭 toolbar
    useEffect(() => {
        const handleClick = (e) => {
            if (!wrapperRef.current?.contains(e.target)) {
                if (!value.trim() && files.length === 0) {
                    setShowToolbar(false);
                }
            }
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [value, files.length]);

    const format = (type, val = true) => {
        const quill = getQuill();
        if (!quill) return;
        quill.format(type, val);
    };

    const sanitizeHtml = (html) => {
        return html
            .replace(/<table[\s\S]*?<\/table>/gi, "")
            .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
            .replace(/<video[\s\S]*?<\/video>/gi, "")
            .replace(/<embed[\s\S]*?>/gi, "")
            .replace(/<object[\s\S]*?<\/object>/gi, "");
    };

    const triggerChange = (html, fileList) => {
        if (!onChange) return;
        const uploadedFiles = fileList
            .filter((f) => f.url)
            .map((f) => ({ name: f.name, url: f.url }));
        onChange({ html: cleanHtml(html), files: uploadedFiles });
    };

    const handleChange = (content) => {
        const cleaned = sanitizeHtml(content);
        setValue(cleaned);
        triggerChange(cleaned, files);
    };

    const clearAllFormat = () => {
        const quill = getQuill();
        if (!quill) return;
        const text = quill.getText();
        quill.setText(text);
    };

    const handleFileChange = async (e) => {
        const selected = Array.from(e.target.files);
        if (files.length + selected.length > maxFiles) {
            dispatch(onErrorAlert(`${t('Max')} ${maxFiles} ${t('files')}`));
            return;
        }
        for (const file of selected) {
            if (!validateFile(file)) continue;
            const id = Date.now() + Math.random();
            setFiles((prev) => [...prev, { id, name: file.name, uploading: true }]);
            try {
                const url = await uploadFile(file);
                setFiles((prev) => {
                    const updated = prev.map((f) => f.id === id ? { ...f, url, uploading: false } : f);
                    triggerChange(getQuill()?.root.innerHTML, updated);
                    return updated;
                });
            } catch {
                setFiles((prev) => {
                    const updated = prev.filter((f) => f.id !== id);
                    triggerChange(getQuill()?.root.innerHTML, updated);
                    return updated;
                });
            }
        }
        e.target.value = null;
    };

    const handleImageInputChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) await insertImageToEditor(file);
        e.target.value = null;
    };

    const hasUploading = files.some((f) => f.uploading) || imageUploading > 0;

    return (
        <MDBox
            ref={wrapperRef}
            sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 1,
                background: "transparent",
                "& .ql-container": { border: "none" },
                "& .ql-editor": {
                    minHeight: minHeight,
                    maxHeight: 200,
                    overflowY: "auto",
                    padding: "12px",
                },
                "& .ql-editor img": {
                    maxWidth: "100%",
                    maxHeight: 300,
                    borderRadius: "4px",
                    objectFit: "contain",
                    display: "block",
                    margin: "4px 0",
                },
            }}
        >
            {/* Quote */}
            {quote && (
                <MDBox
                    sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 0.5,
                        background: "#f3f4f6",
                        borderLeft: "3px solid #cbd5e1",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <span style={{ fontSize: 12, opacity: 0.6 }}>❝</span>
                    <MDTypography
                        sx={{
                            flex: 1,
                            fontSize: 12,
                            color: "#374151",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            minWidth: 0,
                        }}
                    >
                        {quote?.addWho}: {stripHtml(quote?.commentContent)}
                    </MDTypography>
                    <span onClick={onClearQuote} style={{ cursor: "pointer", fontSize: 12, color: "#9ca3af" }}>
                        ✕
                    </span>
                </MDBox>
            )}

            {/* Toolbar */}
            {showToolbar && (
                <MDBox sx={{ display: "flex", gap: 0.5, px: 1, py: 0.5, borderBottom: "1px solid #eee" }}>
                    <IconButton size="small" onClick={() => format("bold")}>
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => format("italic")}>
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => format("underline")}>
                        <FormatUnderlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => format("list", "ordered")}>
                        <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => format("list", "bullet")}>
                        <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={clearAllFormat}>
                        <ClearIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        ref={moreBtnRef}
                        onClick={() => setAnchorEl(moreBtnRef.current)}
                    >
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                </MDBox>
            )}

            {/* Editor */}
            <ReactQuill
                ref={quillRef}
                value={value}
                onChange={handleChange}
                modules={{ toolbar: false }}
                placeholder={t(`TicketManagementPage.Type a message`)}
                style={{ color: darkMode ? "#fff" : "text" }}
            />

            {/* Image uploading indicator */}
            {imageUploading > 0 && (
                <MDBox sx={{ px: 1.5, py: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={12} />
                    <MDTypography sx={{ fontSize: 11, color: "#9ca3af" }}>
                        Uploading image...
                    </MDTypography>
                </MDBox>
            )}

            {/* Attached files */}
            {files.length > 0 && (
                <MDBox sx={{ px: 1.5, py: 0.75, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {files.map((file, i) => (
                        <MDBox
                            key={file.id}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                maxWidth: 220,
                                px: 1,
                                py: "3px",
                                borderRadius: 1,
                                border: "1px solid #d1d5db",
                                background: "#f3f4f6",
                                fontSize: 12,
                                gap: 0.5,
                            }}
                        >
                            <span style={{ fontSize: 12, opacity: 0.7 }}>📄</span>
                            {file.url ? (
                                <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "#374151",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        flex: 1,
                                    }}
                                >
                                    {file.name}
                                </a>
                            ) : (
                                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
                                    {file.name}
                                </span>
                            )}
                            {file.uploading && <span style={{ fontSize: 10, color: "#9ca3af" }}>...</span>}
                            <span
                                onClick={() =>
                                    setFiles((prev) => {
                                        const updated = prev.filter((_, idx) => idx !== i);
                                        triggerChange(getQuill()?.root.innerHTML, updated);
                                        return updated;
                                    })
                                }
                                style={{ cursor: "pointer", fontSize: 12, color: "#9ca3af" }}
                            >
                                ✕
                            </span>
                        </MDBox>
                    ))}
                </MDBox>
            )}

            {/* More menu */}
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => { format("header", 1); setAnchorEl(null); }}>
                    <TitleIcon fontSize="small" sx={{ mr: 1 }} /> H1
                </MenuItem>
                <MenuItem onClick={() => { format("header", 2); setAnchorEl(null); }}>
                    <TitleIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} /> H2
                </MenuItem>
                <MenuItem onClick={() => { format("header", false); setAnchorEl(null); }}>
                    <TitleIcon fontSize="small" sx={{ mr: 1, opacity: 0.5 }} /> Normal text
                </MenuItem>
                <MenuItem disabled>────────</MenuItem>
                <MenuItem onClick={() => { format("align", false); setAnchorEl(null); }}>
                    <FormatAlignLeftIcon fontSize="small" sx={{ mr: 1 }} /> Align left
                </MenuItem>
                <MenuItem onClick={() => { format("align", "center"); setAnchorEl(null); }}>
                    <FormatAlignCenterIcon fontSize="small" sx={{ mr: 1 }} /> Align center
                </MenuItem>
                <MenuItem onClick={() => { format("align", "right"); setAnchorEl(null); }}>
                    <FormatAlignRightIcon fontSize="small" sx={{ mr: 1 }} /> Align right
                </MenuItem>
                <MenuItem disabled>────────</MenuItem>
                <MenuItem onClick={() => { format("color", "red"); setAnchorEl(null); }}>
                    <FormatColorTextIcon fontSize="small" sx={{ mr: 1, color: "red" }} /> Text red
                </MenuItem>
                <MenuItem onClick={() => { format("color", "blue"); setAnchorEl(null); }}>
                    <FormatColorTextIcon fontSize="small" sx={{ mr: 1, color: "blue" }} /> Text blue
                </MenuItem>
                <MenuItem onClick={() => { format("background", "yellow"); setAnchorEl(null); }}>
                    <HighlightIcon fontSize="small" sx={{ mr: 1, color: "#facc15" }} /> Highlight
                </MenuItem>
                <MenuItem disabled>────────</MenuItem>
                <MenuItem onClick={() => { format("blockquote"); setAnchorEl(null); }}>
                    <FormatQuoteIcon fontSize="small" sx={{ mr: 1 }} /> Quote
                </MenuItem>
                <MenuItem onClick={() => { format("code-block"); setAnchorEl(null); }}>
                    <CodeIcon fontSize="small" sx={{ mr: 1 }} /> Code block
                </MenuItem>
            </Menu>

            {/* Bottom bar */}
            <MDBox sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 1, borderTop: "1px solid #eee" }}>
                <MDBox display="flex" gap={0.5} alignItems="center">
                    <IconButton size="small" onClick={() => setShowToolbar((v) => !v)}>
                        <FormatColorTextOutlinedIcon fontSize="small" />
                    </IconButton>

                    <input
                        ref={imageInputRef}
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageInputChange}
                    />

                    <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                        <AttachFileOutlinedIcon fontSize="small" />
                    </IconButton>
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        multiple
                        accept={ACCEPT_TYPES}
                        onChange={handleFileChange}
                    />
                </MDBox>

                <MDBox display="flex" gap={1}>
                    {mode === "edit" && (
                        <Button size="small" onClick={() => onCancel?.()}>
                            Cancel
                        </Button>
                    )}
                    {mode !== "form" && (
                        <Button
                            variant="contained"
                            size="small"
                            disabled={hasUploading}
                            onClick={() => {
                                const rawHtml = getQuill()?.root.innerHTML;
                                const html = cleanHtml(rawHtml);
                                if (!html || html === "<p><br></p>") return;

                                const uploadedFiles = files
                                    .filter((f) => f.url)
                                    .map((f) => ({ name: f.name, url: f.url }));

                                onSend?.({
                                    html,
                                    files: uploadedFiles,
                                    quote: quote
                                        ? {
                                            commentId: quote.commentId,
                                            ownerName: quote.ownerName,
                                            content: quote.commentContent,
                                        }
                                        : null,
                                });

                                if (mode === "send") {
                                    setValue(initialHtml);
                                    setFiles([]);
                                    setTimeout(() => {
                                        const quill = getQuill();
                                        if (!quill) return;
                                        const length = quill.getLength() - 1;
                                        quill.setSelection(length, 0);
                                        quill.focus();
                                    }, 100);
                                } else if (mode !== "edit") {
                                    setValue("");
                                    setFiles([]);
                                }
                            }}
                        >
                            {mode === "edit" ? "Save" : t(`TicketManagementPage.Send`)}
                        </Button>
                    )}
                </MDBox>
            </MDBox>
        </MDBox>
    );
};

export default ReplyEditor;
